import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  linkedSignal,
  signal,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OdlTag } from '@odilo/ui-components';
import { HighContrastMode } from '../../helpers/high-contrast';
import { FeatureFlagsService } from '../../services/feature-flags/feature-flags';
import { FullscreenService } from '../../services/fullscreen/fullscreen';
import { MetadataService } from '../../services/metadata/metadata';
import { OfflineDownloadService } from '../../services/offline/offline-download';
import { ReaderApi } from '../../services/reader-api/reader-api';
import { SettingsService } from '../../services/readium/settings/settings';
import { ToastService } from '../../services/toast/toast';
import { TtsService } from '../../services/tts/tts';

enum LanguageOption {
  SPANISH = 'es',
  ENGLISH = 'en',
}

enum MicrophonePermissionStatus {
  NOT_SUPPORTED = 'NOT_SUPPORTED',
  PENDING = 'PENDING',
  GRANTED = 'GRANTED',
}

@Component({
  selector: 'nr-more-options',
  imports: [TranslatePipe],
  templateUrl: './more-options.html',
  styleUrl: './more-options.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MoreOptionsComponent {
  private readonly metadataService = inject(MetadataService);
  private readonly translateService = inject(TranslateService);
  private readonly readerApi = inject(ReaderApi);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly settingsService = inject(SettingsService);
  private readonly toastService = inject(ToastService);
  private readonly ttsService = inject(TtsService);
  private readonly fullscreenService = inject(FullscreenService);
  private readonly offlineDownloadService = inject(OfflineDownloadService);
  readonly featureFlags = inject(FeatureFlagsService);
  readonly microphoneEnabled = signal(false);
  readonly microphonePermissionStatus = signal<MicrophonePermissionStatus>(
    MicrophonePermissionStatus.PENDING,
  );
  readonly microphonePermissionLabel = computed(() => {
    const status = this.microphonePermissionStatus();
    switch (status) {
      case MicrophonePermissionStatus.NOT_SUPPORTED:
        return 'Not supported';
      case MicrophonePermissionStatus.PENDING:
        return 'Pending';
      case MicrophonePermissionStatus.GRANTED:
        return 'Granted';
      default:
        return '';
    }
  });
  readonly microphonePermissionVariant = computed<OdlTag['variant']>(() => {
    const status = this.microphonePermissionStatus();
    switch (status) {
      case MicrophonePermissionStatus.NOT_SUPPORTED:
        return 'error';
      case MicrophonePermissionStatus.PENDING:
        return 'info';
      case MicrophonePermissionStatus.GRANTED:
        return 'success';
      default:
        return 'default';
    }
  });
  readonly accessibilityMode = signal(false);
  readonly HighContrastMode = HighContrastMode;
  readonly LanguageOption = LanguageOption;
  readonly MicrophonePermissionStatus = MicrophonePermissionStatus;
  readonly userLanguage = this.metadataService.userLanguage;
  readonly highContrastMode = this.settingsService.highContrastMode;

  readonly popoverFor = signal<string>('');

  readonly languageOption = linkedSignal<string>(this.userLanguage);

  readonly isTtsSupported = this.ttsService.isSupported;

  readonly isFullscreen = this.fullscreenService.isFullscreen;

  readonly isOfflineModeEnabled = this.metadataService.isOfflineModeEnabled;

  constructor() {
    this.initializeMicrophoneStatus();
    setTimeout(() => {
      this.breakpointObserver.observe('(min-width: 1200px)').subscribe((result) => {
        this.popoverFor.set(
          result.matches ? '#header-more-options-button' : '#footer-more-options-button',
        );
      });
    });
  }

  toggleFullscreen(): void {
    this.fullscreenService.toggle();
  }

  startAutomatedReading(): void {
    if (!this.ttsService.hasAvailableVoices()) {
      this.toastService.warning(String(this.translateService.instant('TTS.NO_VOICES_AVAILABLE')));
      return;
    }

    this.ttsService.isDialogOpen.set(true);
  }

  private async initializeMicrophoneStatus(): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!navigator.mediaDevices?.getUserMedia) {
      this.microphonePermissionStatus.set(MicrophonePermissionStatus.NOT_SUPPORTED);
      return;
    } else {
      this.microphonePermissionStatus.set(MicrophonePermissionStatus.PENDING);
    }

    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      });
      if (permissionStatus.state === 'granted') {
        this.microphonePermissionStatus.set(MicrophonePermissionStatus.GRANTED);
      } else {
        this.microphonePermissionStatus.set(MicrophonePermissionStatus.PENDING);
      }

      permissionStatus.onchange = (): void => {
        if (permissionStatus.state === 'granted') {
          this.microphonePermissionStatus.set(MicrophonePermissionStatus.GRANTED);
        } else {
          this.microphonePermissionStatus.set(MicrophonePermissionStatus.PENDING);
        }
      };
    } catch {
      this.microphonePermissionStatus.set(MicrophonePermissionStatus.PENDING);
    }
  }

  setMicrophoneStatus(enabled: boolean): void {
    this.microphoneEnabled.set(enabled);
    if (enabled) {
      this.checkMicrophonePermission();
    }
  }

  private checkMicrophonePermission(): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!navigator.mediaDevices?.getUserMedia) {
      this.microphonePermissionStatus.set(MicrophonePermissionStatus.NOT_SUPPORTED);
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        this.microphonePermissionStatus.set(MicrophonePermissionStatus.GRANTED);
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      })
      .catch((error) => {
        console.error('Microphone permission denied:', error);
        this.microphonePermissionStatus.set(MicrophonePermissionStatus.PENDING);
        this.microphoneEnabled.set(false);
      });
  }

  setAccessibilityMode(enabled: boolean): void {
    this.accessibilityMode.set(enabled);
    console.log('TODO: Set accessibility mode to', enabled);
  }

  setHighContrastMode(mode: HighContrastMode): void {
    void this.settingsService.setHighContrastMode(mode);
  }

  async setLanguage(language: string): Promise<void> {
    this.translateService.use(language);
    this.languageOption.set(language);
    await this.readerApi.updateUserLanguage(language);
  }

  openOfflineReadingModal(): void {
    this.offlineDownloadService.isDialogOpen.set(true);
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  openHelp(): void {
    console.log('TODO: Open help');
  }
}
